/**
 * Two implementations of embedding web content, both have drawbacks.
 * WebViewOverlay uses the electron BrowserView api, sized automatically to be positioned inside
 * a div in the DOM.
 * Browser functionality seems to be perfect, but it is rendered as a fully separate view on top
 * of the rest of Vortex and can thus not be overlayed. As such it will also not disappear until
 * unmounted (or set to an empty url).
 * Thus care has to be taken how this is utilized or it will appear broken and janky
 *
 * WebviewEmbed uses the chrome <webview> component which integrates better but doesn't seem to
 * forward all events correcty. Specifically we were not able to handle any event when clicking
 * the download button on google drive. (as of Electron 15.1.1)
 */
import { log } from "../util/log";
import { truthy } from "../util/util";
import { closeBrowserView, makeBrowserView, positionBrowserView, updateViewURL, } from "../webview";
import { ipcRenderer } from "electron";
import { omit } from "lodash";
import * as React from "react";
import ReactDOM from "react-dom";
import { clearInterval } from "timers";
const RESIZE_EVENTS = ["scroll", "resize"];
function BrowserView(props) {
    const viewId = React.useRef();
    const container = React.useRef();
    const bounds = React.useRef();
    const updateViewBounds = React.useCallback(() => {
        if (truthy(container.current)) {
            const rect = container.current.getBoundingClientRect();
            bounds.current = {
                x: Math.round(rect.left),
                y: Math.round(rect.top),
                width: Math.round(rect.width),
                height: Math.round(rect.height),
            };
            positionBrowserView(viewId.current, bounds.current);
        }
    }, []);
    React.useEffect(() => {
        let wasVisible = true;
        const overlapTest = setInterval(() => {
            if (truthy(bounds.current)) {
                // janky way of estimating a position that would be overlapped
                const x = bounds.current.x + bounds.current.width / 2;
                const y1 = bounds.current.y + bounds.current.height * 0.33;
                const y2 = bounds.current.y + bounds.current.height * 0.66;
                const ele1 = document.elementFromPoint(x, y1);
                const ele2 = document.elementFromPoint(x, y2);
                const isVisible = ele1 === container.current && ele2 === container.current;
                if (wasVisible !== isVisible) {
                    positionBrowserView(viewId.current, isVisible ? bounds.current : { x: 0, y: 0, width: 0, height: 0 });
                    wasVisible = isVisible;
                }
            }
        }, 1000);
        return () => {
            clearInterval(overlapTest);
        };
    }, []);
    React.useEffect(() => {
        updateViewURL(viewId.current, props.src);
    }, [props.src]);
    React.useEffect(() => {
        const impl = async () => {
            viewId.current = await makeBrowserView(props.src, Object.keys(props.events), {
                webPreferences: {
                    // Use separate partition to isolate cookies from main window
                    // This prevents external site cookies (e.g., moddb.com) from being
                    // sent to CDN downloads that use signed URLs for authentication
                    partition: "persist:webview",
                },
            });
            RESIZE_EVENTS.forEach((evtId) => {
                window.addEventListener(evtId, updateViewBounds);
            });
            Object.keys(props.events).forEach((evtId) => {
                ipcRenderer.on(`view-${viewId.current}-${evtId}`, (evt, argsJSON) => {
                    props.events[evtId](...JSON.parse(argsJSON));
                });
            });
            updateViewBounds();
        };
        impl();
        return () => {
            closeBrowserView(viewId.current);
            RESIZE_EVENTS.forEach((evtId) => {
                window.removeEventListener(evtId, updateViewBounds);
            });
            Object.keys(props.events).forEach((evtId) => {
                ipcRenderer.removeAllListeners(`view-${viewId.current}-${evtId}`);
            });
        };
    }, []);
    return (React.createElement("div", { ref: container, style: {
            width: "100%",
            height: "100%",
        } }));
}
const emptyObject = {};
export class WebviewOverlay extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            src: props.src,
        };
    }
    UNSAFE_componentWillReceiveProps(newProps) {
        if (this.props.src !== newProps.src) {
            this.setState({ src: newProps.src });
        }
    }
    render() {
        const { events } = this.props;
        return truthy(this.props.src) ? (React.createElement(BrowserView, { src: this.state.src, events: {
                "did-start-loading": this.startLoad,
                "did-stop-loading": this.stopLoad,
                "console-message": this.logMessage,
                "new-window": this.newWindow,
                "enter-html-full-screen": this.enterFullscreen,
                "leave-html-full-screen": this.leaveFullscreen,
                ...(events ?? emptyObject),
            } })) : null;
    }
    loadURL(newUrl) {
        this.setState({ src: newUrl });
    }
    startLoad = () => {
        const { onLoading } = this.props;
        if (onLoading !== undefined) {
            onLoading(true);
        }
    };
    stopLoad = () => {
        const { onLoading } = this.props;
        if (onLoading !== undefined) {
            onLoading(false);
        }
    };
    newWindow = (url, frameName, disposition) => {
        const { onNewWindow } = this.props;
        if (onNewWindow !== undefined) {
            onNewWindow(url, disposition);
        }
    };
    enterFullscreen = () => {
        const { onFullscreen } = this.props;
        if (onFullscreen !== undefined) {
            onFullscreen(true);
        }
    };
    leaveFullscreen = () => {
        const { onFullscreen } = this.props;
        if (onFullscreen !== undefined) {
            onFullscreen(false);
        }
    };
    logMessage = (level, message) => {
        if (level > 2) {
            log("info", "from embedded page", { level, message });
        }
    };
}
export class WebviewEmbed extends React.Component {
    mNode;
    componentDidMount() {
        this.mNode = ReactDOM.findDOMNode(this);
        this.mNode.addEventListener("did-start-loading", this.startLoad);
        this.mNode.addEventListener("did-stop-loading", this.stopLoad);
        this.mNode.addEventListener("dom-ready", () => {
            const id = this.mNode["getWebContentsId"]();
            ipcRenderer.send("webview-dom-ready", id);
            ipcRenderer.on("webview-open-url", (_, idInner, url, disposition) => {
                if (id === idInner) {
                    this.props.onNewWindow?.(url, disposition);
                }
            });
            /*
            (this.mNode as any).insertCSS(
              'body.mediawiki, #content, #mw-pages > table { background-color: #4c4c4c !important }');
            */
            // (this.mNode as any).openDevTools();
        });
        this.mNode.addEventListener("console-message", this.logMessage);
        // this.mNode.addEventListener('new-window', this.newWindow);
        this.mNode.addEventListener("enter-html-full-screen", this.enterFullscreen);
        this.mNode.addEventListener("leave-html-full-screen", this.leaveFullscreen);
    }
    componentWillUnmount() {
        this.mNode.removeEventListener("did-start-loading", this.startLoad);
        this.mNode.removeEventListener("did-stop-loading", this.stopLoad);
        this.mNode.removeEventListener("console-message", this.logMessage);
        // this.mNode.removeEventListener('new-window', this.newWindow);
        this.mNode.removeEventListener("enter-html-full-screen", this.enterFullscreen);
        this.mNode.removeEventListener("leave-html-full-screen", this.leaveFullscreen);
    }
    render() {
        return React.createElement("webview", {
            ...omit(this.props, [
                "onLoading",
                "onNewWindow",
                "onFullscreen",
                "events",
            ]),
            allowpopups: "true",
        });
    }
    loadURL(newUrl) {
        this.mNode["loadURL"](newUrl);
    }
    startLoad = () => {
        const { onLoading } = this.props;
        if (onLoading !== undefined) {
            onLoading(true);
        }
    };
    stopLoad = () => {
        const { onLoading } = this.props;
        if (onLoading !== undefined) {
            onLoading(false);
        }
    };
    newWindow = (evt) => {
        const { onNewWindow } = this.props;
        if (onNewWindow !== undefined) {
            onNewWindow(evt.url, evt.disposition);
        }
    };
    enterFullscreen = (evt) => {
        const { onFullscreen } = this.props;
        if (onFullscreen !== undefined) {
            onFullscreen(true);
        }
    };
    leaveFullscreen = (evt) => {
        const { onFullscreen } = this.props;
        if (onFullscreen !== undefined) {
            onFullscreen(false);
        }
    };
    logMessage = (evt) => {
        if (evt.level > 2) {
            log("info", "from embedded page", {
                level: evt.level,
                message: evt.message,
            });
        }
    };
}
export default WebviewEmbed;
